import type { NamePath } from "antd/es/form/interface"
import dayjs, { type Dayjs } from "dayjs"
import { type Rule } from "rc-field-form/lib/interface"
import { useCallback } from "react"
import { useSelector } from "react-redux"

import {
  ALL_DATE_FORMATS,
  DATE_FORMATS_OBJ
} from "../containers/TeamManagementPage/constants/date-formats.ts"
import { type RootState } from "../store.ts"
import { DATE_FORMAT } from "../utils/date/date.ts"

type DateRuleType = "start" | "end"

interface DateRangeMessages {
  start?: string
  end?: string
}

const defaultMessages: Required<DateRangeMessages> = {
  start: "Start date can't be after end date",
  end: "End date can't be before start date"
}

export const useDateFormat = () => {
  const userInfo = useSelector((state: RootState) => state.auth.userInfo)

  const userFormat = userInfo?.date_format
    ? DATE_FORMATS_OBJ?.[userInfo?.date_format]?.format
    : null

  // Memoised because callers put it in dependency arrays: an unstable identity
  // here makes their `useMemo`/`useCallback` miss on every render, and one of
  // those feeds antd's `components.body.row` — a new identity there is a new
  // component type, so every row unmounts and remounts mid-edit.
  const parseDate = useCallback(
    (value: unknown): Dayjs | null => {
      if (!value) return null
      if (dayjs.isDayjs(value)) return value as Dayjs

      if (value instanceof Date) {
        const d = dayjs(value)
        return d.isValid() ? d : null
      }

      if (typeof value === "string") {
        const isoDate = dayjs(value, "YYYY-MM-DD", true)
        if (isoDate.isValid()) return isoDate

        if (value.includes("T")) {
          const isoDateTime = dayjs(value)
          if (isoDateTime.isValid()) return isoDateTime
        }

        const fallback = dayjs(value, userFormat || ALL_DATE_FORMATS, false)
        if (fallback.isValid()) return fallback
      }

      return null
    },
    [userFormat]
  )

  const toLocalDayKey = (d: Dayjs): string => d.format("YYYY-MM-DD")

  const getDateValueProps = (value: any) => {
    const parsed = parseDate(value)
    return { value: parsed }
  }

  // Normalize any incoming date representation into a single canonical string.
  // By default outputs DATE_FORMAT (ISO YYYY-MM-DD for backend),
  // but pass `useUserFormat: true` to format with the user's preferred format
  // (e.g. for displaying in inputs).
  function normalizeDate(date: Date | Dayjs | string): string
  function normalizeDate(
    date: Date | Dayjs | string | null | undefined,
    format?: string
  ): string | null
  function normalizeDate(
    date: Date | Dayjs | string | null | undefined,
    format?: string
  ): string | null {
    if (!date) return null

    const parsed = parseDate(date)
    if (!parsed || !parsed.isValid()) return null

    return parsed.format(format ?? DATE_FORMAT)
  }

  const createDateRangeRules = <T extends object>(
    compareField: keyof T | NamePath,
    type: DateRuleType,
    messages?: DateRangeMessages
  ): Rule[] => [
    ({ getFieldValue }) => ({
      validator(_, value) {
        const compareValue = getFieldValue(compareField as NamePath)

        if (!value || !compareValue) return Promise.resolve()

        const current = parseDate(value)
        const compare = parseDate(compareValue)

        if (!current || !compare) return Promise.resolve()

        const currentKey = toLocalDayKey(current)
        const compareKey = toLocalDayKey(compare)

        if (currentKey === compareKey) return Promise.resolve()

        const errorMessages = { ...defaultMessages, ...messages }

        if (type === "start" && currentKey > compareKey) {
          return Promise.reject(new Error(errorMessages.start))
        }

        if (type === "end" && currentKey < compareKey) {
          return Promise.reject(new Error(errorMessages.end))
        }

        return Promise.resolve()
      }
    })
  ]

  return {
    userFormat,
    parseDate,
    getDateValueProps,
    createDateRangeRules,
    normalizeDate
  }
}
